export interface DateTimePickerParts {
  date: Date;
  hour: number;
  minute: number;
}

/**
 * Prüft, ob ein Wert ein reines Datum ohne Uhrzeit ist (z.B. "2026-06-20").
 * Solche Werte parsen sonst auf Mitternacht (00:00) — der Picker soll dann
 * eine sinnvolle Default-Uhrzeit anbieten statt 00:00.
 */
export function isDateOnlyValue(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

export function parseDateTimePickerValue(
  value: string | null | undefined
): DateTimePickerParts | null {
  if (!value) return null;

  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    d = new Date(year, month - 1, day, 0, 0, 0, 0);
  } else {
    d = new Date(value);
  }

  if (isNaN(d.getTime())) return null;

  return { date: d, hour: d.getHours(), minute: d.getMinutes() };
}

export function formatDateTimePickerDisplay(
  date: Date,
  hour: number,
  minute: number
): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dateStr = date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `${dateStr}, ${pad(hour)}:${pad(minute)}`;
}

export function buildDateTimeFromParts(
  date: Date,
  hour: number,
  minute: number
): string {
  const d = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hour,
    minute,
    0,
    0
  );
  const pad = (n: number) => n.toString().padStart(2, '0');
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const oh = pad(Math.floor(absMinutes / 60));
  const om = pad(absMinutes % 60);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:${pad(minute)}:00${sign}${oh}:${om}`;
}

export function isPastDateTime(date: Date, hour: number, minute: number): boolean {
  const target = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hour,
    minute,
    0,
    0
  );
  return target < new Date();
}

const WEEKDAY_MAP: Record<string, number> = {
  sonntag: 0,
  montag: 1,
  dienstag: 2,
  mittwoch: 3,
  donnerstag: 4,
  freitag: 5,
  samstag: 6,
};

function parseTime(timeStr: string): { hour: number; minute: number } | null {
  const m = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

function nextWeekday(base: Date, targetDay: number): Date {
  const today = base.getDay();
  let diff = targetDay - today;
  if (diff <= 0) diff += 7;
  return addDays(base, diff);
}

export function parseNaturalLanguageDateTime(
  input: string
): DateTimePickerParts | null {
  const raw = input.trim();
  if (!raw) return null;

  const now = new Date();

  // DD.MM.YYYY HH:mm
  const germanFull = raw.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}:\d{2})$/
  );
  if (germanFull) {
    const day = parseInt(germanFull[1], 10);
    const month = parseInt(germanFull[2], 10) - 1;
    const year = parseInt(germanFull[3], 10);
    const time = parseTime(germanFull[4]);
    if (!time) return null;
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    return { date, ...time };
  }

  // YYYY-MM-DD HH:mm
  const isoFull = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}:\d{2})$/);
  if (isoFull) {
    const year = parseInt(isoFull[1], 10);
    const month = parseInt(isoFull[2], 10) - 1;
    const day = parseInt(isoFull[3], 10);
    const time = parseTime(isoFull[4]);
    if (!time) return null;
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    return { date, ...time };
  }

  // "heute HH:mm"
  const heuteMatch = raw.match(/^heute\s+(\d{1,2}:\d{2})$/i);
  if (heuteMatch) {
    const time = parseTime(heuteMatch[1]);
    if (!time) return null;
    return { date: addDays(now, 0), ...time };
  }

  // "morgen HH:mm"
  const morgenMatch = raw.match(/^morgen\s+(\d{1,2}:\d{2})$/i);
  if (morgenMatch) {
    const time = parseTime(morgenMatch[1]);
    if (!time) return null;
    return { date: addDays(now, 1), ...time };
  }

  // "in N Tag(en)"
  const inTageMatch = raw.match(/^in\s+(\d+)\s+tag(?:en)?$/i);
  if (inTageMatch) {
    const n = parseInt(inTageMatch[1], 10);
    return { date: addDays(now, n), hour: 9, minute: 0 };
  }

  // "Wochentag HH:mm"
  const weekdayMatch = raw.match(/^(\w+)\s+(\d{1,2}:\d{2})$/i);
  if (weekdayMatch) {
    const dayName = weekdayMatch[1].toLowerCase();
    const targetDay = WEEKDAY_MAP[dayName];
    if (targetDay !== undefined) {
      const time = parseTime(weekdayMatch[2]);
      if (!time) return null;
      return { date: nextWeekday(now, targetDay), ...time };
    }
  }

  return null;
}

export function getMinutesForStep(step: number): number[] {
  const result: number[] = [];
  for (let m = 0; m < 60; m += step) {
    result.push(m);
  }
  return result;
}

export function navigateHour(current: number, direction: 'up' | 'down'): number {
  return direction === 'up' ? (current - 1 + 24) % 24 : (current + 1) % 24;
}

export function navigateMinute(
  current: number,
  minutes: number[],
  direction: 'up' | 'down'
): number {
  const idx = minutes.indexOf(current);
  if (idx === -1) return minutes[0] ?? current;
  return direction === 'up'
    ? minutes[(idx - 1 + minutes.length) % minutes.length]
    : minutes[(idx + 1) % minutes.length];
}
